import { defineStore } from 'pinia'
import dayjs from '@/dayjs'

import { api, mock, preferences } from '@/services'
import { useKeygen } from '@/composables'

export const useAuthStore = defineStore('auth', {
    state: () => {
        return {
            token: null,
            userKey: null,
            registered: false,
            lastRefreshedAt: null,
            refreshing: false,
        }
    },
    actions: {
        async init() {
            const { generate } = useKeygen()

            this.token = await preferences.getItem('auth_token')
            this.userKey = await preferences.getItem('user_key')

            if (!this.userKey) {
                // generate a new key
                this.userKey = generate()

                // save it for next time
                await preferences.setItem('user_key', this.userKey)
            }

            this.registered = !!this.token
        },
        async setToken(token) {
            this.token = token
            await preferences.setItem('auth_token', token)
        },
        async devicePayload() {
            const deviceStore = useDeviceStore()
            const appStore = useAppStore()

            const { device } = deviceStore

            return {
                external_id: device.identifier,
                platform: device.platform,
                timezone: await deviceStore.timezone(),
                locale: await deviceStore.locale(),
                os: device.operatingSystem,
                os_version: device.osVersion,
                manufacturer: device.manufacturer,
                model: device.model,
                is_virtual: device.isVirtual,
                web_view_version: device.webViewVersion,
                store_version: appStore.info.version,
                js_version: appStore.appVersion,
                native_version: appStore.nativeIdentity?.appVersion,
            }
        },
        async register() {
            if (this.registered || mock.mocking()) {
                return Promise.resolve()
            }

            const { appId } = useAppStore()
            const payload = await this.devicePayload()

            return api.post(`/${appId}/devices/register`, payload)
                .then(async ({ data }) => {
                    await this.setToken(data.token)
                })
                .catch((e) => {
                    console.log('Something wrong with register?')
                    return Promise.reject(e)
                })
        },
        async update() {
            if (mock.mocking()) {
                return
            }
            const { appId } = useAppStore()
            const payload = await this.devicePayload()

            return api.put(`/${appId}/devices/update`, payload)
        },
        async migrate(payload) {
            const { appId } = useAppStore()

            return api.post(`/${appId}/devices/migrate`, payload)
        },
        async pushRegister(token) {
            const { appId } = useAppStore()

            console.log('Registering push token with api', token)

            return api.post(`/${appId}/push/register`, {
                token,
            })
        },
        async pushUnregister() {
            const { appId } = useAppStore()

            console.log('Unregistering push token with api')

            return api.post(`/${appId}/push/unregister`)
        },
        pushFormat(notification) {
            const { appId } = useAppStore()

            return api.post(`/${appId}/push/format`, notification)
                .then(({ data }) => {
                    return data
                })
        },
        /**
         * @type {?Dayjs} [requestedAt] Optional timestamp of when the request was made, used to determine if the refresh is needed
         * @return {Promise<void>} Resolves when the token is refreshed
         */
        refresh(requestedAt = null) {
            if (this.refreshing) {
                console.log('Already refreshing token, skipping')
                return Promise.resolve()
            }

            // Limit how often we refresh the token
            if (this.lastRefreshedAt && requestedAt && requestedAt.isSameOrBefore(this.lastRefreshedAt)) {
                console.log('Token refresh not required, last refreshed at', this.lastRefreshedAt.toISOString())
                return Promise.resolve()
            }

            const { appId } = useAppStore()

            console.log('Refreshing token')
            this.refreshing = true
            return api.post(`/${appId}/devices/refresh`)
                .then(({ data }) => {
                    this.setToken(data.token)
                    this.lastRefreshedAt = dayjs()
                })
                .catch((e) => {
                    console.log('Refresh failed', e)
                })
                .finally(() => {
                    this.refreshing = false
                })
        },
        logout() {
            console.log('Remove token')
            this.token = null
            preferences.removeItem('auth_token')
        },
    },
})
